// =============================================================================
// features/sales/agenda/components/CompleteAppointmentForm.tsx — Formulário
// de conclusão INLINE do drawer (doc 26 §6.1 "Formulário de conclusão
// inline para evitar troca de página").
//
// Validação espelha `AppointmentCompleteBodySchema.superRefine` (backend,
// packages/shared-schemas/src/sales-appointments.ts) lendo os MESMOS
// `SALES_OUTCOME_META` — outcome que exige observação bloqueia envio vazio;
// `callback_requested` (requiresAppointment) exige data/hora do retorno no
// mesmo fluxo. Nunca duplicamos a regra, só o mesmo dado fonte.
//
// Erro de rede preserva os campos preenchidos (doc 26 §7) — o estado do
// react-hook-form só muda em sucesso; em erro, mostramos o Alert e mantemos
// tudo como estava.
// =============================================================================

import type {
  Appointment,
  AppointmentCompleteBody,
  ContactChannel,
  SalesOutcomeCode,
} from '@elemento/shared-schemas';
import { CONTACT_CHANNELS, SALES_OUTCOME_META } from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { orgLocalToUtcIso } from '../../components/dateTimeOrgTz';
import { FormTextarea } from '../../components/FormTextarea';
import { OutcomeSelector } from '../../components/OutcomeSelector';
import { useCompleteAppointment } from '../hooks';
import { CHANNEL_LABELS } from '../labels';

const CHANNEL_OPTIONS = CONTACT_CHANNELS.map((c: ContactChannel) => ({
  value: c,
  label: CHANNEL_LABELS[c],
}));

// Exportado para teste direto (sem duplicar a regra numa "réplica" —
// mesmo objeto validado em runtime pelo formulário).
export const CompleteFormSchema = z
  .object({
    outcomeCode: z.string().min(1, 'Selecione o desfecho'),
    notes: z.string().max(4000, 'Máximo 4000 caracteres').optional(),
    nextDate: z.string().optional(),
    nextTime: z.string().optional(),
    nextChannel: z.string().optional(),
    nextReason: z.string().max(500, 'Máximo 500 caracteres').optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.outcomeCode) return;
    const meta = SALES_OUTCOME_META[data.outcomeCode as SalesOutcomeCode];

    if (meta.requiresNote && (!data.notes || data.notes.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notes'],
        message: `O desfecho "${meta.label}" exige observação para concluir.`,
      });
    }

    if (meta.requiresAppointment && (!data.nextDate || !data.nextTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nextDate'],
        message: `O desfecho "${meta.label}" exige agendar a data e hora do retorno.`,
      });
    }
  });

type CompleteFormValues = z.infer<typeof CompleteFormSchema>;

export interface CompleteAppointmentFormProps {
  appointment: Appointment;
  onDone: () => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function CompleteAppointmentForm({
  appointment,
  onDone,
  onCancel,
  onDirtyChange,
}: CompleteAppointmentFormProps): React.JSX.Element {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<CompleteFormValues>({
    resolver: zodResolver(CompleteFormSchema),
    defaultValues: {
      outcomeCode: '',
      notes: '',
      nextDate: '',
      nextTime: '',
      nextChannel: '',
      nextReason: '',
    },
  });

  React.useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const outcomeCode = watch('outcomeCode');
  const meta = outcomeCode ? SALES_OUTCOME_META[outcomeCode as SalesOutcomeCode] : undefined;

  const { mutate, isPending } = useCompleteAppointment({
    onSuccess: () => {
      setSubmitError(null);
      onDone();
    },
    onError: (error) =>
      setSubmitError(error.message || 'Não foi possível concluir. Tente novamente.'),
  });

  function onSubmit(values: CompleteFormValues): void {
    const body: AppointmentCompleteBody = {
      outcomeCode: values.outcomeCode as SalesOutcomeCode,
      ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
    };

    if (meta?.requiresAppointment && values.nextDate && values.nextTime) {
      const scheduledAt = orgLocalToUtcIso(values.nextDate, values.nextTime);
      if (!scheduledAt) {
        setSubmitError('Data e hora do retorno inválidas.');
        return;
      }
      body.nextAppointment = {
        scheduledAt,
        ...(values.nextChannel ? { channel: values.nextChannel as ContactChannel } : {}),
        ...(values.nextReason?.trim() ? { reason: values.nextReason.trim() } : {}),
      };
    }

    setSubmitError(null);
    mutate({ id: appointment.id, body });
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <OutcomeSelector
        id="complete-outcome"
        required
        value={(outcomeCode as SalesOutcomeCode | '') ?? ''}
        onChange={(value) =>
          setValue('outcomeCode', value, { shouldValidate: true, shouldDirty: true })
        }
        error={errors.outcomeCode?.message}
        disabled={isPending}
      />

      <FormTextarea
        id="complete-notes"
        label="Observação"
        required={meta?.requiresNote ?? false}
        placeholder="Registre o contexto do desfecho"
        disabled={isPending}
        error={errors.notes?.message}
        {...register('notes')}
      />

      {meta?.requiresAppointment && (
        <div
          className="flex flex-col gap-4 rounded-sm px-4 py-4"
          style={{ background: 'var(--bg-elev-2)', boxShadow: 'var(--elev-1)' }}
        >
          <p className="font-sans text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
            Novo retorno (obrigatório)
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              id="complete-next-date"
              label="Data"
              type="date"
              required
              disabled={isPending}
              error={errors.nextDate?.message}
              {...register('nextDate')}
            />
            <Input
              id="complete-next-time"
              label="Hora"
              type="time"
              required
              disabled={isPending}
              {...register('nextTime')}
            />
          </div>
          <Select
            id="complete-next-channel"
            label="Canal do retorno"
            placeholder={`Mesmo canal (${CHANNEL_LABELS[appointment.channel]})`}
            options={CHANNEL_OPTIONS}
            disabled={isPending}
            {...register('nextChannel')}
          />
          <Input
            id="complete-next-reason"
            label="Motivo do retorno"
            placeholder="Opcional"
            disabled={isPending}
            {...register('nextReason')}
          />
        </div>
      )}

      {submitError && (
        <div
          role="alert"
          className="rounded-sm px-4 py-3 border-l-[3px]"
          style={{
            borderColor: 'var(--danger)',
            background: 'var(--danger-bg)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <p className="font-sans font-semibold text-sm text-danger">Erro ao concluir</p>
          <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
        </div>
      )}

      <div className="flex gap-2.5">
        <Button
          type="submit"
          variant="primary"
          disabled={isPending}
          className="flex-1 justify-center"
        >
          {isPending ? 'Concluindo…' : submitError ? 'Tentar novamente' : 'Concluir compromisso'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
          Voltar
        </Button>
      </div>
    </form>
  );
}
